<template>
  <v-main full-height>
    <v-container style="height: 100%" class="flex flex-col" full-height fluid>
      <div>
        <div v-if="user.supervisorOrgId" class="flex items-center">
          <v-btn
            icon="mdi-arrow-left"
            variant="text"
            size="small"
            :to="breadcrumbItems[breadcrumbItems.length - 2]?.to"
          ></v-btn>
          <v-breadcrumbs
            active-color="primary"
            active-class="text-xl"
            :items="breadcrumbItems"
          ></v-breadcrumbs>
        </div>

        <template v-if="loading">
          <v-skeleton-loader width="100px" type="heading" />
          <v-skeleton-loader type="heading" />
        </template>
        <template v-else-if="beneficiary">
          <div class="flex flex-col my-3 ml-3">
            <div class="text-3xl text-high-emphasis">
              {{ beneficiary.netAmountFormatted }}
            </div>
            <div class="text-sm text-medium-emphasis mt-1">{{ beneficiary.iban }}</div>
          </div>
          <v-tabs class="mb-6 mt-3" color="primary">
            <v-tab
              :to="{
                name: 'BeneficiaryTransactions',
                params: { accountId: accountId, beneficiaryIban: beneficiaryIban },
              }"
              >{{ t("beneficiaryDetail.tabs.transactions") }}</v-tab
            >
            <v-tab
              :to="{
                name: 'BeneficiaryTransferRequest',
                params: { accountId: accountId, beneficiaryIban: beneficiaryIban },
              }"
              >{{ t("account.tabs.transfersToValidate") }}
              <v-badge
                v-if="pendingTransfersCount > 0"
                color="warning"
                :content="pendingTransfersCount"
                inline
              ></v-badge>
            </v-tab>
          </v-tabs>
        </template>
      </div>
      <div class="flex flex-col px-1" style="flex: 1 1 0; overflow: auto; position: relative">
        <router-view v-slot="{ Component }">
          <keep-alive>
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </div>
      <PendingTransferRequestDetailDrawer :account-id="accountId" />
      <TransactionDetailDrawer :account-id="accountId" :show-beneficiary-button="false" />
    </v-container>
  </v-main>
</template>

<script lang="ts" setup>
import { useAccountCurrency } from "@/composables/useAccountCurrency.ts";
import { useBankAccount } from "@/composables/useBankAccount";
import { useCollection } from "@/composables/useFirestore.ts";
import { useI18n } from "@/composables/useI18n";
import { useTransactions } from "@/composables/useTransactions.ts";
import { useTransferRequests } from "@/composables/useTransferRequests.ts";
import { db } from "@/firebase.ts";
import { useUserStore } from "@/store/user";
import { formatCurrency } from "@/utils/tools";
import type { Beneficiary } from "@/utils/types";
import {
  treezorBeneficiariesFullPath,
  type Beneficiary as TreezorBeneficiary,
} from "@aircap/common/treezor";
import { collection, limit, query, where } from "firebase/firestore";
import { computed, toRef, watch } from "vue";
import PendingTransferRequestDetailDrawer from "../../components/PendingTransferRequestDetailDrawer.vue";
import TransactionDetailDrawer from "../../components/TransactionDetailDrawer.vue";

const props = defineProps<{
  accountId: string;
  beneficiaryIban: string;
}>();

const { t, locale } = useI18n();
const user = useUserStore();
const accountId = toRef(props, "accountId");
const beneficiaryIban = toRef(props, "beneficiaryIban");

const { currencyCode, accountType } = useAccountCurrency(accountId);
const { account } = useBankAccount(accountId);

const beneficiariesSource = computed(() => {
  switch (accountType.value) {
    case "treezor":
      return query(
        collection<TreezorBeneficiary>(db, treezorBeneficiariesFullPath(accountId.value)),
        where<TreezorBeneficiary>("metadata.iban", "==", beneficiaryIban.value),
        limit(1),
      );
    case "swan":
    case "bridge":
    case "powens":
    case "gcl":
    case undefined:
      return null;
  }
});
const { data: rawBeneficiaries, pending: loadingBeneficiaries } =
  useCollection<Omit<TreezorBeneficiary, "id">>(beneficiariesSource);

const { transactions, loading: loadingTransactions } = useTransactions(accountId, {
  beneficiaryIban,
  isFuture: toRef(() => false),
});

const { transferRequests, loadingTransferRequests } = useTransferRequests(accountId, {
  beneficiaryIban,
  status: toRef(() => "pending"),
});

const loading = computed(
  () => loadingBeneficiaries.value || loadingTransactions.value || loadingTransferRequests.value,
);

const pendingTransfersCount = computed(() => {
  if (!transferRequests.value) return 0;
  return transferRequests.value.length;
});

const beneficiary = computed<Pick<Beneficiary, "iban" | "name" | "netAmountFormatted"> | null>(
  () => {
    if (!currencyCode.value) return null;

    return {
      iban: beneficiaryIban.value,
      name:
        rawBeneficiaries.value?.[0]?.name ||
        transferRequests.value?.[0]?.beneficiaryName ||
        transactions.value.find(({ counterparty }) => !!counterparty)?.counterparty ||
        beneficiaryIban.value,
      netAmountFormatted: formatCurrency({
        value: transactions.value.reduce((acc, transaction) => acc + transaction.amount, 0),
        currencyCode: currencyCode.value,
        sign: true,
        locale: locale.value,
      }),
    };
  },
);

watch(
  [account, beneficiary, beneficiaryIban],
  ([account, beneficiary, beneficiaryIban]) => {
    document.title = [beneficiary?.name || beneficiaryIban, account?.name, "Aircap"]
      .filter((x) => x)
      .join(" - ");
  },
  { immediate: true },
);

const breadcrumbItems = computed(() => [
  {
    title: t("account.backToAccounts"),
    to: { name: "Accounts" },
  },
  {
    title: account.value?.name || "",
    to: { name: "Account", params: { accountId: accountId.value } },
  },
  {
    title: t("account.tabs.beneficiaries"),
    to: { name: "Beneficiaries", params: { accountId: accountId.value } },
  },
  {
    title: beneficiary.value?.name || beneficiaryIban.value,
    to: {
      name: "BeneficiaryDetail",
      params: { accountId: accountId.value, beneficiaryIban: beneficiaryIban.value },
    },
  },
]);
</script>

<style scoped>
:deep(.v-breadcrumbs-item--active) {
  opacity: 1 !important;
}
</style>
